import { TUI_CONFIRM } from '@taiga-ui/kit';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { XAlertService } from '../x-alert.service';
import { RequestProductRequiredFieldsService } from './request-product-required-fields.service';
import { RequestsApiService } from './requests-api.service';
import { RequestService } from './request.service';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { CompleteForciblyModalFormComponent } from '@features/requests/request-form/components/request-form-goods/components/';
import {
  CommonTranslationsPayload,
  RequestProduct,
  RequestProductsServiceTranslations,
  RequestProductsServiceTranslationsPayload,
} from '@core/types';
import {
  EMPTY,
  filter,
  forkJoin,
  iif,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class RequestProductAndGoodsService {
  private readonly requestService = inject(RequestService);
  private readonly requestApiService = inject(RequestsApiService);
  private readonly dialogService$ = inject(TuiDialogService);
  private readonly xAlertService = inject(XAlertService);
  private readonly requestProductRequiredFieldsService = inject(
    RequestProductRequiredFieldsService,
  );
  private readonly destroyRef = inject(DestroyRef);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private translates: Partial<RequestProductsServiceTranslationsPayload> = {
    [RequestProductsServiceTranslations.Error]: 'Ошибка',
    [RequestProductsServiceTranslations.ErrorSomethingWentWrong]:
      'Что-то пошло не так при закрытии продуктов! Попробуйте ещё раз',
    [RequestProductsServiceTranslations.SuccessProductsStatusChanged]:
      'Статус продуктов успешно изменён',
    [RequestProductsServiceTranslations.ErrorCancelForciblyCompletionRequirementsNotMet]:
      'Статус продуктов успешно изменён',
    [RequestProductsServiceTranslations.DialogCancelForciblyCompletion]:
      'Вы действительно хотите отменить принудительное выполнение?',
    [RequestProductsServiceTranslations.DialogCancelForciblyCompletionYes]:
      'Да, отменить',
    [RequestProductsServiceTranslations.DialogCancelForciblyCompletionNo]:
      'Нет',
    [RequestProductsServiceTranslations.SuccessCancelForciblyCompletion]:
      'Принудительное выполнение успешно отменено',
    [RequestProductsServiceTranslations.DialogCancelCompletionYes]:
      'Да, отменить',
    [RequestProductsServiceTranslations.DialogCompletionYes]: 'Да, выполнено',
    [RequestProductsServiceTranslations.DialogCompletionNo]: 'Нет',
    [RequestProductsServiceTranslations.SuccessStatusChanged]:
      'Статус продукта успешно изменён',
  };
  readonly $request = this.requestService.$request;

  constructor() {
    this.outkeepTranslateService
      .translate(
        Object.keys(this.translates) as (keyof CommonTranslationsPayload)[],
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));
  }

  public performToggleRequestProductCompletion$(
    requestProduct: RequestProduct,
    multipleProducts = false,
  ): Observable<any> {
    if (requestProduct.completed) {
      return this.callToggleProductCompletion$(
        requestProduct,
        multipleProducts,
      ).pipe(
        switchMap(() =>
          this.requestApiService.cancelRequestProductForcedClosure$(
            requestProduct.id,
          ),
        ),
      );
    } else if (
      this.$request().needToCheckRequiredFields &&
      !requestProduct.isClosedForcingly
    ) {
      return this.requestProductRequiredFieldsService
        .performRequiredFieldsChecking$(requestProduct)
        .pipe(
          switchMap(result => {
            if (result.isSuccess) {
              return this.callToggleProductCompletion$(
                requestProduct,
                multipleProducts,
              );
            } else {
              if (!requestProduct.failedAttemptToClose) {
                return this.requestApiService
                  .setFailedAttemptToCompleteProduct$(requestProduct.id)
                  .pipe(
                    tap(() => this.requestService.reloadRequest()),
                    switchMap(() => EMPTY),
                  );
              }

              return of(null);
            }
          }),
        );
    } else {
      return this.callToggleProductCompletion$(
        requestProduct,
        multipleProducts,
      );
    }
  }

  tryToCompleteAllProducts(requestProducts: RequestProduct[] = []) {
    const failedProductsToClose: string[] = [];

    const productObservables: Observable<any>[] = requestProducts.map(
      (product: RequestProduct) => {
        if (
          !product.completed &&
          !product.failedAttemptToClose &&
          !product.isClosedForcingly
        ) {
          return this.performToggleRequestProductCompletion$(
            product,
            true,
          ).pipe(
            catchError(() => {
              failedProductsToClose.push(product.requestProductId.name);

              return of(null);
            }),
          );
        }
        if (product.isClosedForcingly) {
          return this.cancelForciblyCompletion(product, true).pipe(
            catchError(() => {
              failedProductsToClose.push(product.requestProductId.name);

              return of(null);
            }),
          );
        }
        failedProductsToClose.push(product.requestProductId.name);

        return of(null);
      },
    );

    return iif(
      () => failedProductsToClose.length > 0,
      this.outkeepTranslateService.translate(
        [
          RequestProductsServiceTranslations.ErrorCannotCloseProductsNotValidRequiredFieldsOrForciblyClosed,
        ],
        { failedProductsToClose: failedProductsToClose.join(', ') },
      ),
      of({}),
    ).pipe(
      switchMap(dynamicTranslations => {
        const translations =
          dynamicTranslations as RequestProductsServiceTranslationsPayload;

        return forkJoin(productObservables).pipe(
          catchError(() => {
            if (failedProductsToClose.length) {
              this.xAlertService.showMessage(
                translations[
                  RequestProductsServiceTranslations
                    .ErrorCannotCloseProductsNotValidRequiredFieldsOrForciblyClosed
                ],
                {
                  appearance: 'warning',
                  autoClose: 0,
                },
              );
            }
            this.xAlertService.error(
              this.translates[RequestProductsServiceTranslations.Error]!,
              this.translates[
                RequestProductsServiceTranslations.ErrorSomethingWentWrong
              ]!,
              false,
            );

            this.requestService.reloadRequest();

            return EMPTY;
          }),
          tap(() => {
            this.requestService.reloadRequest();
            if (failedProductsToClose.length === 0) {
              this.xAlertService.showMessage(
                this.translates[
                  RequestProductsServiceTranslations
                    .SuccessProductsStatusChanged
                ],
                {
                  appearance: 'positive',
                },
              );
            } else {
              this.xAlertService.showMessage(
                translations[
                  RequestProductsServiceTranslations
                    .ErrorCannotCloseProductsNotValidRequiredFieldsOrForciblyClosed
                ],
                {
                  appearance: 'warning',
                  autoClose: 0,
                },
              );
            }
          }),
        );
      }),
    );
  }

  public showCompleteForciblyModalForm(
    requestProduct: RequestProduct,
    problemsText: string,
  ): Observable<void> {
    return this.dialogService$.open<void>(
      new PolymorpheusComponent(CompleteForciblyModalFormComponent),
      {
        closable: false,
        dismissible: false,
        data: { requestProduct, problemsText },
      },
    );
  }

  public performForciblyCompletion$(
    requestProductId: RequestProduct['id'],
    reason: string,
  ): Observable<void> {
    return this.requestApiService.closeProductForsibly$(
      requestProductId,
      reason,
    );
  }

  public completeProduct$(
    requestProductId: RequestProduct['id'],
    status: boolean,
  ): Observable<void> {
    return this.requestApiService.changeProductCompletionStatus$(
      requestProductId,
      status,
    );
  }

  public cancelForciblyCompletion(
    requestProduct: RequestProduct,
    multipleProducts = false,
  ): Observable<void> {
    if (multipleProducts) {
      return this.outkeepTranslateService
        .translate(
          [RequestProductsServiceTranslations.ErrorCancelForciblyCompletion],
          { productName: requestProduct.requestProductId.name },
        )
        .pipe(
          switchMap(dynamicTranslations => {
            return this.requestProductRequiredFieldsService
              .performRequiredFieldsChecking$(requestProduct)
              .pipe(
                switchMap(result => {
                  if (!result.isSuccess) {
                    this.xAlertService.showMessage(
                      `<div class="typo-body-m font-extrabold">
                    ${dynamicTranslations[RequestProductsServiceTranslations.ErrorCancelForciblyCompletion]}
                    <div class="goods-problems-list-container">${result.helpText}</div>
                </div>`,
                      {
                        appearance: 'warning',
                        autoClose: 0,
                      },
                    );

                    return EMPTY;
                  }

                  return of(true);
                }),
                switchMap(() =>
                  this.requestApiService.cancelRequestProductForcedClosure$(
                    requestProduct.id,
                  ),
                ),
              );
          }),
        );
    }

    return this.requestProductRequiredFieldsService
      .performRequiredFieldsChecking$(requestProduct)
      .pipe(
        switchMap(result => {
          if (!result.isSuccess) {
            this.xAlertService.showMessage(
              this.translates[
                RequestProductsServiceTranslations
                  .ErrorCancelForciblyCompletionRequirementsNotMet
              ]!,
              {
                appearance: 'warning',
                autoClose: 5000,
              },
            );
            this.xAlertService.showMessage(
              `<div class="goods-problems-list-container">${result.helpText}</div>`,
              {
                appearance: 'negative',
                autoClose: 0,
              },
            );

            return EMPTY;
          }

          return of(result);
        }),
        switchMap(() =>
          this.dialogService$.open<boolean>(TUI_CONFIRM, {
            label:
              this.translates[
                RequestProductsServiceTranslations
                  .DialogCancelForciblyCompletion
              ],
            size: 's',
            closable: false,
            data: {
              yes: this.translates[
                RequestProductsServiceTranslations
                  .DialogCancelForciblyCompletionYes
              ],
              no: this.translates[
                RequestProductsServiceTranslations
                  .DialogCancelForciblyCompletionNo
              ],
            },
          }),
        ),
        filter(Boolean),
        switchMap(() =>
          this.requestApiService.cancelRequestProductForcedClosure$(
            requestProduct.id,
          ),
        ),
        tap(() => {
          this.xAlertService.showMessage(
            this.translates[
              RequestProductsServiceTranslations.SuccessCancelForciblyCompletion
            ],
            { appearance: 'positive' },
          );
          this.requestService.reloadRequest();
        }),
      );
  }

  private callToggleProductCompletion$(
    requestProduct: RequestProduct,
    multipleProducts: boolean,
  ): Observable<void> {
    if (multipleProducts) {
      return this.completeProduct$(
        requestProduct.id,
        !requestProduct.completed,
      );
    } else {
      return this.outkeepTranslateService
        .translate(
          [
            RequestProductsServiceTranslations.DialogCancelCompletion,
            RequestProductsServiceTranslations.DialogCompletion,
          ],
          { productName: requestProduct.requestProductId.name },
        )
        .pipe(
          switchMap(dynamicTranslations => {
            return this.dialogService$
              .open<boolean>(TUI_CONFIRM, {
                label: requestProduct.completed
                  ? dynamicTranslations[
                      RequestProductsServiceTranslations.DialogCancelCompletion
                    ]
                  : dynamicTranslations[
                      RequestProductsServiceTranslations.DialogCompletion
                    ],
                size: 's',
                closable: false,
                data: {
                  yes: requestProduct.completed
                    ? this.translates[
                        RequestProductsServiceTranslations
                          .DialogCancelCompletionYes
                      ]
                    : this.translates[
                        RequestProductsServiceTranslations.DialogCompletionYes
                      ],
                  no: this.translates[
                    RequestProductsServiceTranslations.DialogCompletionNo
                  ],
                },
              })
              .pipe(
                filter(Boolean),
                switchMap(() =>
                  this.completeProduct$(
                    requestProduct.id,
                    !requestProduct.completed,
                  ),
                ),
                tap(() => {
                  this.requestService.reloadRequest();
                  this.xAlertService.showMessage(
                    this.translates[
                      RequestProductsServiceTranslations.SuccessStatusChanged
                    ]!,
                    {
                      appearance: 'positive',
                    },
                  );
                }),
              );
          }),
        );
    }
  }
}
